import React from "react";
import "../style/DateTimeRange.scss";
import PropTypes from "prop-types";
import momentPropTypes from "react-moment-proptypes";
import { generateHours, generateMinutes } from "../utils/TimeFunctionUtils";
import { addFocusStyle, darkTheme, lightTheme } from "../utils/StyleUtils";
import Icons from "../../../../icons";
import moment from 'moment';

class TimeField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hourFocus: false,
      minuteFocus: false,
      showHourDropdown: false,
      showMinuteDropdown: false,
      showMeridiemDropdown: false,
      formattedDateObjects: [],
    };
    this.hourRefs = [];
    this.minuteRefs = [];
    this.meridiemRefs = [];
    this.dropdownRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    this.updateFormattedDates(this.props.customSelectedDateTime);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.customSelectedDateTime !== this.props.customSelectedDateTime) {
      this.updateFormattedDates(this.props.customSelectedDateTime);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  // Method to format each ISO string to 'DD-MM-YYYY HH:mm'
  formatDates = (isoArray) => {
    if (isoArray) {
      return isoArray.map((isoString) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
      });
    }
  };

  updateFormattedDates = (isoArray) => {
    const formattedDates = this.formatDates(isoArray);
    this.setState({ formattedDateObjects: formattedDates });
  };

  // Function to convert each ISO string to a Date object
  convertToDates = (isoArray) => {
    if (isoArray) {
      return isoArray.map((isoString) => new Date(isoString));
    }
  };

  extractHoursAndMinutes = (dateStrings) => {
    if(dateStrings){
      return dateStrings.map(dateStr => {
        const dateObj = moment(dateStr, "DD-MM-YYYY HH:mm");

        let hours = dateObj.hour();
        const minutes = dateObj.minute();
        
        const meridiem = dateObj.format("A");
        if (this.props.twelveHoursClock) {
          hours = hours % 12 || 12;
        }
        
        return [hours, minutes];
      });
    }
  };

  
  handleClickOutside = (event) => {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({
        showHourDropdown: false,
        showMinuteDropdown: false,
        showMeridiemDropdown: false,
      });
    }
  };

  toggleHourDropdown = () => {
    this.setState(
      (prevState) => ({
        showHourDropdown: !prevState.showHourDropdown,
        showMinuteDropdown: false,
        showMeridiemDropdown: false,
      }),
      () => {
        if (this.state.showHourDropdown) {
          this.scrollToSelected("hour");
        }
      }
    );
  };

  toggleMinuteDropdown = () => {
    this.setState(
      (prevState) => ({
        showMinuteDropdown: !prevState.showMinuteDropdown,
        showHourDropdown: false,
        showMeridiemDropdown: false,
      }),
      () => {
        if (this.state.showMinuteDropdown) {
          this.scrollToSelected("minute");
        }
      }
    );
  };

  toggleMeridiemDropdown = () => {
    this.setState(
      (prevState) => ({
        showMeridiemDropdown: !prevState.showMeridiemDropdown,
        showHourDropdown: false,
        showMinuteDropdown: false,
      }),
      () => {
        if (this.state.showMeridiemDropdown) {
          this.scrollToSelected("meridiem");
        }
      }
    );
  };

  scrollToSelected = (type) => {
    if (type === "hour" && this.hourRefs[this.getHourIndex()]) {
      this.hourRefs[this.getHourIndex()].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } else if (type === "minute" && this.minuteRefs[this.props.date.minute()]) {
      this.minuteRefs[this.props.date.minute()].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } else if (type === "meridiem" && this.meridiemRefs[this.props.date.format("A")]) {
      this.meridiemRefs[this.props.date.format("A")].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  getHourIndex = () => {
    const hour = this.props.date.hour();
    if (this.props.twelveHoursClock) {
      return hour % 12 === 0 ? 11 : (hour % 12) - 1;
    }
    return hour;
  };

  mapToButtons = (variableArray, type) => {
    return variableArray.map((varInstance, i) => (
      <button
        key={i}
        onClick={() => this.handleButtonClick(type, varInstance)}
        ref={(el) => {
          if (type === "hour") {
            this.hourRefs[i] = el;
          } else if (type === "minute") {
            this.minuteRefs[i] = el;
          } else if (type === "meridiem") {
            this.meridiemRefs[varInstance] = el;
          }
        }}
        className={
          (type === "hour" && i === this.getHourIndex()) ||
          (type === "minute" && i === this.props.date.minute()) ||
          (type === "meridiem" && varInstance === this.props.date.format("A"))
            ? "selected"
            : ""
        }
        style={{
          marginTop: "2px",
          marginBottom: "2px",
          backgroundColor: this.props.darkMode ? "#333" : "#fff",
          color: this.props.darkMode ? "#fff" : "#000",
          cursor: "pointer",
          width: "100%",
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = "#7159A6";
          e.target.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = this.props.darkMode ? "#333" : "#fff";
          e.target.style.color = "#000";
        }}
      >
        {type === "minute" ? varInstance.toString().padStart(2, "0") : varInstance}
      </button>
    ));
  };

  handleButtonClick = (type, value) => {
    if (type === "hour") {
      this.props.timeChangeCallback(
        this.props.twelveHoursClock
          ? this.convertHourUsingMeridiem(parseInt(value), this.props.date.format("A"))
          : parseInt(value),
        this.props.date.minute(),
        this.props.mode
      );
      this.setState({ showHourDropdown: false });
    } else if (type === "minute") {
      this.props.timeChangeCallback(
        this.props.date.hour(),
        parseInt(value),
        this.props.mode
      );
      this.setState({ showMinuteDropdown: false });
    } else if (type === "meridiem") {
      this.props.timeChangeCallback(
        this.convertHourUsingMeridiem(parseInt(this.props.date.format("h")), value),
        this.props.date.minute(),
        this.props.mode
      );
      this.setState({ showMeridiemDropdown: false });
    }
  };

  convertHourUsingMeridiem = (hour, meridiem) => {
    if (meridiem === "PM" && hour !== 12) {
      return hour + 12;
    } else if (meridiem === "AM" && hour === 12) return 0;
    else return hour;
  };

  render() {
    const theme = this.props.darkMode ? darkTheme : lightTheme;
    const hours = generateHours(this.props.twelveHoursClock);
    const minutes = generateMinutes();
    const meridiems = ["AM", "PM"];

    const hourFocusStyle = addFocusStyle(this.state.hourFocus, {});
    const minuteFocusStyle = addFocusStyle(this.state.minuteFocus, {});
    const meridiemFocusStyle = addFocusStyle(this.state.meridiemFocus, {});

    const dateObjects = this.convertToDates(this.props.customSelectedDateTime);
    const formattedDateObjects = this.formatDates(dateObjects);

    const hoursAndMinutes = this.extractHoursAndMinutes(formattedDateObjects);

    return (
      <div className="timeContainer" ref={this.dropdownRef}>
        <div className="timeSelectContainer">
          {Icons.clockTimeIcon}
          <div
            className={
              this.state.showHourDropdown
                ? "multipleContentOnLine custom opened leftAlignment hour"
                : "multipleContentOnLine custom hour"
            }
            onFocus={() => this.setState({ hourFocus: true })}
            onBlur={() => this.setState({ hourFocus: false })}
            style={hourFocusStyle}
          >
            <div
              onClick={this.toggleHourDropdown}
              style={{ cursor: "pointer", ...theme }}
            >
              {/* <span className="valueSelected hour">

                {this.props.twelveHoursClock
                  ? parseInt(this.props.date.format("h"))
                  : this.props.date.hour()}

              </span> */}

              <span className="valueSelected hour">{this.props.twelveHoursClock
                ? parseInt(this.props.date.format("h"))
                : this.props.date.hour()}</span>
              <span className="downArrowIcon">
                {Icons.chevronDownArrowIcon}
                </span>

              {/* <span className="downArrowIcon">{Icons.chevronDownArrowIcon}</span> */}
            </div>
            {this.state.showHourDropdown && (
              <div className="selectedDropdown">{this.mapToButtons(hours, "hour")}</div>
            )}
          </div>
          <div>:</div>
          <div
            className={
              this.state.showMinuteDropdown
                ? "multipleContentOnLine custom opened leftAlignment15 hour"
                : "multipleContentOnLine custom"
            }
            onFocus={() => this.setState({ minuteFocus: true })}
            onBlur={() => this.setState({ minuteFocus: false })}
            style={minuteFocusStyle}
          >
            <div
              onClick={this.toggleMinuteDropdown}
              style={{ cursor: "pointer", ...theme }}
            >
              {/* <span className="valueSelected">

                {this.props.date.minute().toString().padStart(2, "0")}
              </span> */}

                <span className="valueSelected">{this.props.date.minute().toString().padStart(2, '0')}</span>
                <span className="downArrowIcon">
                  {Icons.chevronDownArrowIcon}
                </span>
                
              {/* <span className="downArrowIcon">{Icons.chevronDownArrowIcon}</span> */}
            </div>
            {this.state.showMinuteDropdown && (
              <div className="selectedDropdown">{this.mapToButtons(minutes, "minute")}</div>
            )}
          </div>
          {this.props.twelveHoursClock && (
            <div
              className={
                this.state.showMeridiemDropdown
                  ? "multipleContentOnLine custom opened leftAlignment15 hour meridian"
                  : "multipleContentOnLine custom meridian"
              }
              onFocus={() => this.setState({ meridiemFocus: true })}
              onBlur={() => this.setState({ meridiemFocus: false })}
              style={meridiemFocusStyle}
            >
              <div
                onClick={this.toggleMeridiemDropdown}
                style={{ cursor: "pointer", ...theme }}
              >
                <span className="valueSelected">{this.props.date.format("A")}</span>
                <span className="downArrowIcon">{Icons.chevronDownArrowIcon}</span>
              </div>
              {this.state.showMeridiemDropdown && (
                <div className="selectedDropdown">{this.mapToButtons(meridiems, "meridiem")}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

TimeField.propTypes = {
  timeChangeCallback: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  date: momentPropTypes.momentObj,
  darkMode: PropTypes.bool,
  twelveHoursClock: PropTypes.bool,
};

export default TimeField;
